import type { ReactNode } from "react";
import { useMemo } from "react";
import { useThree } from "@react-three/fiber";
import { getWashiTexture } from "./washi";
import { SURFACE, RO } from "../theme3d";
import { PanelOcclusionProvider } from "./PanelOcclusionContext";

export interface ShojiBackingProps {
  width: number;
  height: number;
  cols?: number;
  rows?: number;
  // true (default): a diegetic object of the dōjō — depthTest on, the
  // katana can occlude it. false: an always-on-top system overlay (e.g.
  // PauseScreen over frozen gameplay) — depthTest off, RO.overlay* band.
  // See theme3d.ts for the full convention.
  occluded?: boolean;
  // Wood frame box behind the washi surface — receives real scene
  // lighting (the andon flicker), giving the panel physical depth and a
  // stereo depth cue. Same material combo as Dojo.tsx's ShojiPanel, so no
  // new shader program compiles when a panel first mounts.
  frame?: boolean;
  children?: ReactNode;
}

// The shared backing for every panel in the redesign: a procedural
// washi-on-wood-lattice surface (washi.ts) lit to look like it belongs to
// the dōjō, opaque so text never again sits directly on the raw scene
// (the HANDOFF's core diagnosis). No billboard — panels are mounted
// objects, not screen-space UI; see the plan's "no billboard" decision.
export function ShojiBacking({
  width,
  height,
  cols = 4,
  rows = 3,
  occluded = true,
  frame = true,
  children,
}: ShojiBackingProps) {
  const gl = useThree((s) => s.gl);
  const texture = useMemo(
    () =>
      getWashiTexture({
        width,
        height,
        cols,
        rows,
        maxAnisotropy: gl.capabilities.getMaxAnisotropy(),
      }),
    [width, height, cols, rows, gl],
  );

  const frameRenderOrder = occluded ? RO.panelFrame : RO.overlayBacking;
  const surfaceRenderOrder = occluded ? RO.panelSurface : RO.overlayBacking + 1;

  return (
    <PanelOcclusionProvider value={occluded}>
      <group>
        {frame && (
          <mesh position={[0, 0, -0.016]} renderOrder={frameRenderOrder}>
            <boxGeometry args={[width + 0.04, height + 0.04, 0.03]} />
            <meshStandardMaterial color={SURFACE.frameWood} roughness={0.9} />
          </mesh>
        )}
        <mesh renderOrder={surfaceRenderOrder}>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial map={texture} depthTest={occluded} depthWrite={occluded} />
        </mesh>
        {children}
      </group>
    </PanelOcclusionProvider>
  );
}
